/**
 * useChatSSE.tsx - React hook wrapper around the chat SSE helper
 *
 * Manages streaming state for a single SSE chat request and exposes
 * convenience state to the UI while still allowing per-call handlers.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import {
  ChatSSEParams,
  ChatSSEHandlers,
  ChatSSEConnection,
  openChatSSE,
  StatusEventPayload,
  ChunkEventPayload,
  CompleteEventPayload,
  DoneEventPayload,
  ErrorEventPayload,
  VectorSearchCompletePayload,
  RootPostCreatedPayload,
  UserMessageSavedPayload,
} from "@/api/chatSseClient";

export interface UseChatSSEOptions {
  onStatus?: (payload: StatusEventPayload) => void;
  onRootPostCreated?: (payload: RootPostCreatedPayload) => void;
  onUserMessageSaved?: (payload: UserMessageSavedPayload) => void;
  onVectorSearchComplete?: (payload: VectorSearchCompletePayload) => void;
  onDone?: (payload: DoneEventPayload) => void;
  onError?: (payload: ErrorEventPayload) => void;
}

export interface UseChatSSEReturn {
  status: string;
  streamingText: string;
  isComplete: boolean;
  error: string | null;
  assistantPostId: string | null;
  start: (params: ChatSSEParams, handlers?: Partial<ChatSSEHandlers>) => void;
  disconnect: () => void;
}

export const useChatSSE = (options: UseChatSSEOptions = {}): UseChatSSEReturn => {
  const [status, setStatus] = useState<string>("");
  const [streamingText, setStreamingText] = useState<string>("");
  const [isComplete, setIsComplete] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [assistantPostId, setAssistantPostId] = useState<string | null>(null);

  const connectionRef = useRef<ChatSSEConnection | null>(null);

  const disconnect = useCallback(() => {
    if (connectionRef.current) {
      connectionRef.current.close();
      connectionRef.current = null;
    }
  }, []);

  const start = useCallback(
    (params: ChatSSEParams, handlersOverrides: Partial<ChatSSEHandlers> = {}) => {
      // Clean up any previous connection
      disconnect();

      setStatus("");
      setStreamingText("");
      setIsComplete(false);
      setError(null);
      setAssistantPostId(null);

      const handlers: ChatSSEHandlers = {
        onStatus: (payload) => {
          setStatus(payload.message);
          if (options.onStatus) options.onStatus(payload);
        },
        onRootPostCreated: (payload) => {
          if (options.onRootPostCreated) options.onRootPostCreated(payload);
        },
        onUserMessageSaved: (payload) => {
          if (options.onUserMessageSaved) options.onUserMessageSaved(payload);
        },
        onVectorSearchComplete: (payload) => {
          if (options.onVectorSearchComplete) options.onVectorSearchComplete(payload);
        },
        onChunk: (payload: ChunkEventPayload) => {
          setAssistantPostId(payload.assistantPostId);
          setStreamingText((prev) => prev + payload.chunk);
          if (handlersOverrides.onChunk) handlersOverrides.onChunk(payload);
        },
        onComplete: (payload: CompleteEventPayload) => {
          setAssistantPostId(payload.assistantPostId);
          setStreamingText(payload.fullText);
          setIsComplete(true);
          if (handlersOverrides.onComplete) handlersOverrides.onComplete(payload);
        },
        onDone: (payload: DoneEventPayload) => {
          if (options.onDone) options.onDone(payload);
          if (handlersOverrides.onDone) handlersOverrides.onDone(payload);
          // Single-request lifecycle: close the SSE connection once done
          disconnect();
        },
        onError: (payload: ErrorEventPayload) => {
          setError(payload.error);
          if (options.onError) options.onError(payload);
          if (handlersOverrides.onError) handlersOverrides.onError(payload);
          // Stop reconnect loops on server-side error
          disconnect();
        },
        onConnectionError: (event) => {
          setError("Connection lost");
          if (handlersOverrides.onConnectionError) {
            handlersOverrides.onConnectionError(event);
          }
          // Close the EventSource so the browser doesn't keep reconnecting
          disconnect();
        },
      };

      connectionRef.current = openChatSSE(params, handlers);
    },
    [disconnect, options]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    status,
    streamingText,
    isComplete,
    error,
    assistantPostId,
    start,
    disconnect,
  };
};


